import React, { useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import './Navigation.css';

const getLearnItems = (sportId: string) => {
  const base = [
    { path: '/fundamentals', label: 'Fundamentals', icon: '📚' },
    { path: '/drills', label: 'Drills', icon: '⚡' },
    { path: '/strategies', label: 'Strategies', icon: '🧠' },
    { path: '/equipment', label: 'Equipment', icon: '🛠️' },
  ];
  if (sportId === 'hiking') {
    return [...base, { path: '/trail-prep', label: 'Trail Prep', icon: '🥾' }];
  }
  return base;
};

const Navigation: React.FC = () => {
  const { currentSport, theme } = useTheme();
  const { user } = useAuth();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [learnOpen, setLearnOpen] = useState(false);

  const learnItems = getLearnItems(currentSport.id);
  const isLearnActive = learnItems.some(item => location.pathname === item.path || location.pathname.startsWith(item.path + '/'));

  const getNavItems = () => {
    const baseItems: { path: string; label: string; icon: string; dropdown?: typeof learnItems }[] = [
      { path: '/', label: 'Home', icon: '🏠' },
      { path: '/skill-assessment', label: 'Skills', icon: '📊' },
      { path: '/fundamentals', label: 'Learn', icon: '📚', dropdown: learnItems },
      { path: `/recovery-rehab/${currentSport.id}`, label: 'Recovery & Rehab', icon: '🏥' },
      { path: '/game-analysis', label: 'Your Online Coach', icon: '👨‍🏫' },
    ];

    if (currentSport.id === 'pickleball') {
      return [...baseItems, { path: '/find-players', label: 'Find Players', icon: '👥' }];
    }
    return baseItems;
  };

  const navItems = getNavItems();

  return (
    <nav className="navigation">
      <div className="container">
        <div className="nav-content">
          <div className="nav-brand">
            <span className="nav-sport-icon">
              {currentSport.id === 'pickleball' ? (
                <img 
                  src="/pickleball-icon.svg" 
                  alt="Pickleball Logo" 
                  className="nav-logo-small"
                />
              ) : currentSport.id === 'golf' ? (
                <span className="sport-emoji">⛳</span>
              ) : currentSport.id === 'tennis' ? (
                <img 
                  src="/tennis-icon.svg" 
                  alt="Tennis Logo" 
                  className="nav-logo-small"
                />
              ) : currentSport.id === 'hiking' ? (
                <span className="sport-emoji">🥾</span>
              ) : currentSport.id === 'padel' ? (
                <img 
                  src="/padel-icon.svg" 
                  alt="Padel Logo" 
                  className="nav-logo-small"
                />
              ) : currentSport.id === 'badminton' ? (
                <img 
                  src="/badminton-icon.svg" 
                  alt="Badminton Logo" 
                  className="nav-logo-small"
                />
              ) : currentSport.id === 'racquetball' ? (
                <img 
                  src="/racquetball-icon.svg" 
                  alt="Racquetball Logo" 
                  className="nav-logo-small"
                />
              ) : currentSport.id === 'squash' ? (
                <img 
                  src="/squash-icon.svg" 
                  alt="Squash Logo" 
                  className="nav-logo-small"
                />
              ) : (
                <span className="sport-emoji">🎾</span>
              )}
            </span>
            <span className="nav-sport-name">{currentSport.name}</span>
          </div>
          
          <button 
            className="hamburger-menu"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle navigation menu"
          >
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
            <span className={`hamburger-line ${isMenuOpen ? 'open' : ''}`}></span>
          </button>
          
          <ul className={`nav-menu ${isMenuOpen ? 'open' : ''}`}>
            {navItems.map((item) => (
              <li key={item.path} className={`nav-item ${item.dropdown ? 'nav-item-dropdown' : ''}`}>
                {item.dropdown ? (
                  <div
                    className="nav-dropdown-trigger"
                    onMouseEnter={() => setLearnOpen(true)}
                    onMouseLeave={() => setLearnOpen(false)}
                  >
                    <span
                      className={`nav-link ${isLearnActive ? 'active' : ''}`}
                      style={{
                        color: isLearnActive ? 'white' : 'rgba(255,255,255,0.9)',
                        borderBottomColor: isLearnActive ? 'white' : 'transparent'
                      }}
                      onClick={() => { setLearnOpen(!learnOpen); setIsMenuOpen(false); }}
                    >
                      <span className="nav-icon">{item.icon}</span>
                      <span className="nav-label">{item.label}</span>
                      <span className="nav-dropdown-arrow">▼</span>
                    </span>
                    {(learnOpen || isMenuOpen) && (
                      <ul className="nav-dropdown">
                        {item.dropdown.map((sub) => (
                          <li key={sub.path}>
                            <NavLink
                              to={sub.path}
                              className={({ isActive }) => `nav-dropdown-link ${isActive ? 'active' : ''}`}
                              onClick={() => { setLearnOpen(false); setIsMenuOpen(false); }}
                            >
                              <span className="nav-icon">{sub.icon}</span>
                              {sub.label}
                            </NavLink>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <NavLink
                    to={item.path}
                    className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                    style={({ isActive }) => ({
                      color: isActive ? 'white' : 'rgba(255,255,255,0.9)',
                      borderBottomColor: isActive ? 'white' : 'transparent'
                    })}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    <span className="nav-icon">{item.icon}</span>
                    <span className="nav-label">{item.label}</span>
                  </NavLink>
                )}
              </li>
            ))}
          </ul>
          
          <div className="nav-actions">
            {user ? (
              <NavLink
                to="/profile"
                className="nav-profile-link"
              >
                👤 Profile
              </NavLink>
            ) : (
              <>
                <Link to="/signin" className="nav-auth-link">
                  Sign In
                </Link>
                <Link to="/signup" className="nav-auth-link nav-auth-link-outline">
                  Create Account
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;
